import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { existsFilter } from '@iotv/core-services';
import { isClassDerivedDeviceEnabledType } from '@iotv/datamodel';
import { batch, DeviceConfig, getChildEdgeTypeId, HistoryStore, NodeEdgeMapper, } from '@iotv/datamodel-core';
import { AdjacentType, ErrDataReducer, getObjectHash, isHistoryObject, isNode, Milliseconds } from '@iotv/iotv-v3-types';
import { Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useContext, useEffect } from 'react';
import { BinaryChoiceRadio } from '../../../../../../components/common/BinaryChoice';
import { ConsumerDao } from '../../../../../../data/ConsumerDAO';
import { getObjectHistory } from '../../../../../../data/daoFunctions/daoFunctions';
import { useModularTimePeriodSelector } from '../../../../../../hooks/useModularTimePeriodSelector';
import { AdminCardWrapper } from '../../common/AdminCardWrapper';
import { Key } from '../../OperationalStatus/components/Key';
import { ScheduleTimelineChart } from '../../OperationalStatus/components/ScheduleTimelineChart';
import { checkBadRows } from '../../OperationalStatus/fns/checkBadDates';
import { getDataTable } from '../../OperationalStatus/fns/getDataTable';
import { GroupOperationalStateReducerContext } from '../Reducers/GroupOperationalReducer';
const debug = process.env.REACT_APP_DEBUG && false;
/** input to this is shaped by original usage in LSC group view, the entities are extracted from viewObject, when using elsewhere note this. */
export const GroupOperationalStatusInternal = ({ viewObject }) => {
    const { components: TimePeriodSelector, fromDateValue, toDateValue, setDateRangeMs, } = useModularTimePeriodSelector({
        rangeUnit: 'hours',
        rangeLength: 12,
        allowedRangeUnits: ['days', 'hours'],
        behaviourFlags: { preventPeriodShiftIntoFuture: false },
        initialToTime: dayjs().add(6, 'h'),
    });
    const [{ rows, entityStateMap, useReportedRequested }, dispatch] = useContext(GroupOperationalStateReducerContext);
    /* avoid calling with all entities, use unretrievedEntries */
    const retrieveObjectHistories = async (entities) => {
        const clonedEntities = [...entities];
        while (clonedEntities.length > 0) {
            const batch = clonedEntities.splice(0, 10);
            const promised = (await Promise.all(batch
                .map(async (entity) => {
                const { err, data } = await getObjectHistory({
                    vob: entity,
                    objectHistoryFilter: { recentStateHistory: { lastInNMs: Milliseconds.DAY * 60 } },
                });
                if (err) {
                    console.log(`WARN: err in getObjectHistory`, err.message);
                    return null;
                }
                if (data) {
                    if (isHistoryObject(data?.recentStateHistory)) {
                        const recentStateHistory = new HistoryStore(data.recentStateHistory);
                        const entityStateObject = { entity, recentStateHistory, device: null, deviceConfig: null };
                        return entityStateObject;
                    }
                    else {
                        console.log(`WARN not objectHistory`, data.recentStateHistory);
                        return null;
                    }
                }
                else {
                    return null;
                }
            })
                .filter(existsFilter)));
            const action = { type: 'UpdateEntityStates', update: { entityStates: promised } };
            dispatch(action);
        }
    };
    const retrieveEntityContexts = async (entities) => {
        const clonedEntities = [...entities];
        const batchedEntities = batch({ Items: clonedEntities, batchSize: 20 });
        console.log(`INFO: retrieveEntityContexts has batchedEntities`, batchedEntities);
        const promised = await Promise.all(batchedEntities.map(async (batch_i, i) => {
            return ConsumerDao.getEntityContext({ entities: batch_i, includeCustomerGroups: false, includeDeviceConfigs: true });
        }));
        const { err, data: consolidatedRes } = ErrDataReducer(promised);
        if (err) {
            console.log(`ERR: retrieveEntityContexts`, err.message);
        }
        if (consolidatedRes) {
            console.log(`INFO: retrieveEntityContexts consolidatedRes`, consolidatedRes);
            const consolidatedNodeEdgeMapper = consolidatedRes.reduce((a, c) => {
                const { nodeEdgeMapper } = c;
                a.union(nodeEdgeMapper);
                return a;
            }, new NodeEdgeMapper());
            const entityStateObjects = entities.reduce((a, entity) => {
                const device = consolidatedNodeEdgeMapper
                    .getAdjacentNodes(entity, {
                    objectTypeId: 'Device',
                    adjacencyType: AdjacentType.CHILD,
                    edgeTypeId: getChildEdgeTypeId(entity.type, 'Device'),
                })
                    .at(0);
                let deviceConfig = null;
                if (device) {
                    const deviceConfigProxy = DeviceConfig.createForDevice(device);
                    deviceConfig = consolidatedNodeEdgeMapper.getItem(deviceConfigProxy) ?? null;
                }
                a.push({ entity, device: device ?? null, deviceConfig: deviceConfig ? deviceConfig : null, recentStateHistory: null });
                return a;
            }, []);
            const action = { type: 'UpdateEntityStates', update: { entityStates: entityStateObjects } };
            debug && console.log(`INFO: retrieveEntityContexts dispatching `, action);
            dispatch(action);
        }
        else {
            console.log(`WARN: retrieveEntityContexts no consolidatedRes`);
        }
    };
    const entities = viewObject.matchedRelatedBySk?.filter(isNode).filter(isClassDerivedDeviceEnabledType) ?? [];
    const entitiesHash = getObjectHash(entities);
    useEffect(() => {
        const unRetrievedEntities = entities.filter((e) => !entityStateMap[e.sk]);
        console.log(`INFO GroupOperationalStatusInternal using effect on entities hash to retrieve readings and entity contexts`, {
            entities,
            unRetrievedEntities,
        });
        if (unRetrievedEntities.length > 0) {
            dispatch({ type: 'ClearEntityStates', update: undefined });
            retrieveObjectHistories(unRetrievedEntities);
            retrieveEntityContexts(unRetrievedEntities);
        }
    }, [entitiesHash]);
    useEffect(() => {
        debug && console.log(`GroupOperationalStatusInternal used effect on interval`, fromDateValue, toDateValue);
        const action = {
            type: 'SetDates',
            update: { fromDateValue, toDateValue },
        };
        dispatch(action);
    }, [fromDateValue, toDateValue]);
    const sortByFirstScheduled = false;
    if (sortByFirstScheduled) {
        rows?.sort((r1, r2) => {
            const r1First = r1.find((range) => range[1] === 'Scheduled');
            const r2First = r2.find((range) => range[1] === 'Scheduled');
            const both = !!r1First && !!r2First;
            return both ? (r1First[3] < r2First[3] ? -1 : 1) : -1;
        });
    }
    else {
        // Sort by name
        rows?.sort((a, b) => {
            return a[0][0].localeCompare(b[0][0]);
        });
    }
    const flatRows = rows?.flat() ?? null;
    if (process.env.NODE_ENV === 'development')
        checkBadRows(flatRows);
    const dataTable = flatRows ? getDataTable({ rows: flatRows }) : null;
    const tableHeight = (entities?.length ?? 1) * 41 + 50;
    const timelineOptions = {
        hAxis: {
            format: 'HH:mm\nd/MM/yyyy',
        },
        timeline: {
            showRowLabels: true,
        },
        alternatingRowStyle: false,
    };
    return (_jsx(AdminCardWrapper, { children: _jsxs(Stack, { gap: 1, children: [_jsx(Stack, { direction: 'row', children: _jsxs(Typography, { variant: "h5", children: [viewObject.matchedPrimary?.name ?? '', " schedules"] }) }), _jsxs(Stack, { direction: 'row', justifyContent: 'space-between', children: [_jsxs(Stack, { direction: 'row', gap: 1, children: [_jsx(BinaryChoiceRadio, { labelA: 'Requested',
                                    labelB: 'Reported',
                                    currentValue: useReportedRequested,
                                    valueA: 'REQUESTED',
                                    valueB: 'REPORTED',
                                    onChange: (e) => dispatch({ type: 'SetReportedRequest', update: { useReportedRequested: e.target.value } }) }), _jsx(Key, {})] }), _jsxs(Stack, { direction: 'row', children: [TimePeriodSelector.shiftEarlierBtn, _jsx("div", { style: { marginInlineStart: '0.5em', marginInlineEnd: '1em' }, children: TimePeriodSelector.periodField }), TimePeriodSelector.rangeUnitRadio, TimePeriodSelector.shiftLaterBtn] })] }), _jsx(ScheduleTimelineChart, { dataTable,
                    setDateRangeMs,
                    timelineOptions,
                    fragmentProps: {
                        style: { height: tableHeight, width: '100%' },
                    } })] }) }));
};
